import { FC, type ReactElement } from 'react';
import {
  CartesianGrid,
  LabelList,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from 'recharts';
import type { VisaoGeralMes } from '@/types/dashboards';
import { chaveMeta, type PontoDiarioFormato } from '@/utils/equiplexVisaoGeral';
import { fmtCompacto, fmtData, fmtDiaMes, fmtUn } from '@/utils/painelFormat';
import { useMostrarValores } from '@/hooks/useMostrarValores';
import { mostrarIndices, pontosEmDestaque, ultimoPonto, valoresDe } from '@/utils/rotulosGrafico';
import BotaoValores from '../BotaoValores';
import { ALTURA_GRAFICO, FONTE_EIXO, corCategoria, tickEixo, type CoresPainel } from '../chartTheme';
import PainelCard from '../PainelCard';
import { rotuloDoPonto } from '../rotuloBarra';
import TooltipBox, { type LinhaTooltip } from '../TooltipBox';

interface Props {
  testId: string;
  titulo: string;
  serie: PontoDiarioFormato[];
  /** Formatos desta linha com dado no mês — viram chips e linhas. */
  formatos: string[];
  /** Todos os formatos do painel: fixam a cor de cada um (a mesma da aba S&OP). */
  todas: string[];
  ocultos: string[];
  onAlternar: (formato: string) => void;
  temPlano: boolean;
  mes: VisaoGeralMes;
  corte: string;
  cores: CoresPainel;
}

const num = (v: unknown): number | null => (typeof v === 'number' ? v : null);

function linhasTooltip(p: PontoDiarioFormato, visiveis: string[], cor: (f: string) => string): LinhaTooltip[] {
  return visiveis.flatMap((f) => {
    const real = num(p[f]);
    const meta = num(p[chaveMeta(f)]);
    const linhas: LinhaTooltip[] = [{ rotulo: `${f} produzido`, valor: real === null ? 'sem dado' : fmtUn(real), cor: cor(f) }];
    if (meta !== null) linhas.push({ rotulo: `${f} meta S&OP`, valor: fmtUn(meta) });
    return linhas;
  });
}

interface OpcoesLinhas {
  serie: PontoDiarioFormato[];
  visiveis: string[];
  temPlano: boolean;
  cor: (f: string) => string;
  corTexto: string;
  todos: boolean;
}

const fmtMeta = (v: number): string => `meta ${fmtCompacto(v)}`;

/** Produzido com número no último dia e nos extremos (todos com "Mostrar valores"); meta só no fim. */
function linhas({ serie, visiveis, temPlano, cor, corTexto, todos }: OpcoesLinhas): ReactElement[] {
  return visiveis.flatMap((f) => {
    const destaque = mostrarIndices(pontosEmDestaque(valoresDe(serie, f)), todos);
    const elementos: ReactElement[] = [
      <Line
        key={f}
        type="linear"
        dataKey={f}
        name={f}
        stroke={cor(f)}
        strokeWidth={2}
        dot={{ r: 2.5, strokeWidth: 0, fill: cor(f) }}
        activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--bg-card)' }}
        connectNulls={false}
        isAnimationActive={false}
      >
        <LabelList dataKey={f} content={rotuloDoPonto(fmtCompacto, corTexto, destaque)} />
      </Line>,
    ];
    if (temPlano) {
      const fim = mostrarIndices(ultimoPonto(valoresDe(serie, chaveMeta(f))), false);
      elementos.push(
        <Line
          key={chaveMeta(f)}
          type="linear"
          dataKey={chaveMeta(f)}
          name={`${f} meta`}
          stroke={cor(f)}
          strokeWidth={1.5}
          strokeDasharray="6 4"
          strokeOpacity={0.8}
          dot={false}
          activeDot={false}
          isAnimationActive={false}
        >
          <LabelList dataKey={chaveMeta(f)} content={rotuloDoPonto(fmtMeta, corTexto, fim)} />
        </Line>,
      );
    }
    return elementos;
  });
}

/**
 * Produção diária por formato — o primeiro gráfico do "Daily Tracker" do Manus, agora com
 * dado real e com a meta S&OP do dia tracejada na cor do formato. Os chips em cima são a
 * legenda e o liga/desliga de cada formato; todos abrem ligados.
 */
const ProducaoDiariaFormatoChart: FC<Props> = ({
  testId,
  titulo,
  serie,
  formatos,
  todas,
  ocultos,
  onAlternar,
  temPlano,
  mes,
  corte,
  cores,
}) => {
  const cor = (f: string) => corCategoria(f, todas, cores.modo);
  const visiveis = formatos.filter((f) => !ocultos.includes(f));
  const [todos, alternarTodos] = useMostrarValores();
  const corteNoMes = mes.em_andamento && corte >= mes.start && corte < mes.end;

  const conteudoTooltip = ({ active, payload, label }: TooltipContentProps) => {
    const p = active && payload?.[0] ? (payload[0].payload as PontoDiarioFormato) : null;
    if (!p) return null;
    return <TooltipBox titulo={fmtData(String(label))} linhas={linhasTooltip(p, visiveis, cor)} />;
  };

  return (
    <PainelCard
      testId={testId}
      titulo={titulo}
      subtitulo={
        temPlano
          ? 'Unidades reportadas nas OPs, pelo dia de início (linha cheia), contra a meta S&OP do dia (tracejada).'
          : 'Unidades reportadas nas OPs, pelo dia de início. Mês sem plano S&OP no Balanceamento: sem meta.'
      }
      acoes={<BotaoValores ativo={todos} onAlternar={alternarTodos} testId={`${testId}-valores`} />}
    >
      <div role="group" aria-label={`Formatos — ${titulo}`} className="flex flex-wrap items-center gap-1.5">
        {formatos.map((f) => {
          const ativo = !ocultos.includes(f);
          return (
            <button
              key={f}
              type="button"
              aria-pressed={ativo}
              onClick={() => onAlternar(f)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors ${
                ativo
                  ? 'border-theme-border bg-surface-hover text-content-primary'
                  : 'border-transparent text-content-muted hover:text-content-primary'
              }`}
            >
              <span
                aria-hidden
                className="inline-block h-2.5 w-2.5 rounded-sm"
                style={ativo ? { backgroundColor: cor(f) } : { border: `1.5px solid ${cor(f)}` }}
              />
              {f}
            </button>
          );
        })}
        {temPlano && (
          <span className="ml-2 inline-flex items-center gap-1.5 text-xs text-content-muted">
            <span aria-hidden className="inline-block w-5 border-t-2 border-dashed border-content-muted" />
            meta S&OP do dia
          </span>
        )}
      </div>
      <div style={{ width: '100%', height: ALTURA_GRAFICO }}>
        {formatos.length === 0 || visiveis.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-content-muted">
            {formatos.length === 0 ? 'Nada produzido nem planejado nesta linha no mês.' : 'Ligue ao menos um formato.'}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={serie} margin={{ top: 24, right: 40, bottom: 0, left: 0 }}>
              <CartesianGrid vertical={false} stroke={cores.grade} />
              <XAxis
                dataKey="data"
                tickFormatter={fmtDiaMes}
                tick={tickEixo(cores)}
                axisLine={{ stroke: cores.eixo }}
                tickLine={false}
                minTickGap={16}
              />
              <YAxis tickFormatter={fmtCompacto} tick={tickEixo(cores)} axisLine={false} tickLine={false} width={64} />
              {corteNoMes && (
                <ReferenceLine
                  x={corte}
                  stroke={cores.textoMuted}
                  strokeDasharray="3 3"
                  label={{ value: `reportado até ${fmtDiaMes(corte)}`, position: 'insideTopRight', fill: cores.textoMuted, fontSize: FONTE_EIXO - 1 }}
                />
              )}
              <Tooltip content={conteudoTooltip} cursor={{ stroke: cores.textoMuted, strokeWidth: 1 }} />
              {linhas({ serie, visiveis, temPlano, cor, corTexto: cores.texto, todos })}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </PainelCard>
  );
};

export default ProducaoDiariaFormatoChart;
